/* ============================================================================
   LIVE DATA SOURCE — swap the console's data plane from mock to live.
   ----------------------------------------------------------------------------
   The ported design screens all read from the in-memory store (window.DB via the
   Q selectors). "Going live" is therefore a DATA swap, not a UI swap: we rebuild
   the store from the live QA bundle and the ORIGINAL screens render it unchanged
   (Overview, Circles + calendar + finance, Circle detail, Reports, Host view…).

   The backend is single-tenant, so live mode models ONE organisation ("Yperea")
   whose circles are the live meditations and whose users are the operator + the
   meditation hosts. Admin domains with no backend query (payouts, moderation,
   SAR, billing, announcements, audit) become empty. Metrics with no source
   (occupancy capacity, per-circle income beyond the operator's own contributions)
   read 0. See documentation/plan/07 §11.
   ============================================================================ */
import type { LiveBundle, LiveMed } from './liveStore';

export const LIVE_ORG_ID = 'org_yperea';
const CONSOLE_LS = 'yperea_console_v1';

export interface LiveOperator {
  id: string;
  username: string;
  full_name?: string | null;
  email?: string | null;
}

const PALETTE = ['#95CEF6', '#E991EE', '#FDA949', '#A6D487', '#7FB8E8', '#6CC2F0'];
function colorPair(seed: string): [string, string] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return [PALETTE[h % PALETTE.length], PALETTE[(h >> 3) % PALETTE.length]];
}
function dateOnly(iso?: string | null): string {
  return (iso || '').slice(0, 10);
}
function timeOf(iso?: string | null): string {
  const d = new Date(iso || '');
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
function scheduleStr(iso?: string | null, repeat?: string | null): string {
  const d = new Date(iso || '');
  if (Number.isNaN(d.getTime())) return '—';
  const MM = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const base = `${d.getDate()} ${MM[d.getMonth()]} · ${timeOf(iso)}`;
  return repeat ? `${base} · ${repeat}` : base;
}

type Row = Record<string, unknown>;

/** Rebuild window.DB from the live bundle so the real screens render live data. */
export function hydrateLiveStore(bundle: LiveBundle, operator: LiveOperator): void {
  const DB = (window as unknown as { DB?: Record<string, unknown[]> & Record<string, unknown> }).DB;
  if (!DB) return;

  // --- single live tenant, no departments ---
  DB.organisations = [
    { id: LIVE_ORG_ID, name: 'Yperea', kind: 'global', plan: 'live', status: 'active', accent: '#95CEF6' },
  ];
  DB.departments = [];

  // --- users: operator (super_admin) + meditation hosts (host) ---
  const usersById = new Map<string, Row>();
  usersById.set(operator.id, {
    id: operator.id,
    name: operator.full_name || operator.username || 'You',
    email: operator.email || '',
    role: 'super_admin',
    orgId: LIVE_ORG_ID,
    deptId: null,
    status: 'active',
    last: 'now',
    mfa: true,
    avatar: colorPair(operator.id),
  });
  const ensureHost = (id: string, username?: string | null): string => {
    if (!usersById.has(id)) {
      usersById.set(id, {
        id,
        name: username || 'host',
        email: '',
        role: 'host',
        orgId: LIVE_ORG_ID,
        deptId: null,
        status: 'active',
        last: '—',
        mfa: false,
        avatar: colorPair(id),
      });
    }
    return id;
  };

  // --- circles + sessions from live meditations (operator's own first, then public) ---
  const circles: Row[] = [];
  const sessions: Row[] = [];
  const seen = new Set<string>();
  const meds: LiveMed[] = [...bundle.myUpcoming, ...bundle.myPast, ...bundle.raw.publicMeds.items];
  meds.forEach((m) => {
    if (seen.has(m.id)) return;
    seen.add(m.id);
    const hostId = ensureHost(m.user?.id || operator.id, m.user?.username);
    const cap = m.count_participants || 0;
    const joined = m.count_live_participants || 0;
    circles.push({
      id: m.id,
      name: m.title || 'Untitled meditation',
      hostId,
      orgId: LIVE_ORG_ID,
      deptId: null,
      capacity: cap,
      status: 'active',
      schedule: scheduleStr(m.start_time, m.repeat_interval),
      plannedMin: m.duration,
      audioMode: m.central_object || 'live',
      // explicit one-off occurrence for the calendar (Q.occurrences live branch)
      liveDate: dateOnly(m.start_time),
      liveTime: timeOf(m.start_time),
    });
    sessions.push({
      id: 's_' + m.id,
      circleId: m.id,
      hostId,
      orgId: LIVE_ORG_ID,
      deptId: null,
      date: dateOnly(m.start_time),
      minutes: m.duration,
      attendees: joined,
      capacity: cap,
      occupancy: cap > 0 ? joined / cap : 0,
      invited: cap,
      joined,
      completions: 0,
      plannedMin: m.duration,
      actualMin: m.duration,
      reactions: {},
    });
  });
  // own flat circle groups → circles with no sessions
  (bundle.raw.myCircles || []).forEach((c) => {
    if (seen.has(c.id)) return;
    seen.add(c.id);
    const hostId = ensureHost(c.user?.id || operator.id, c.user?.username);
    circles.push({
      id: c.id,
      name: c.name,
      hostId,
      orgId: LIVE_ORG_ID,
      deptId: null,
      capacity: c.count_members || 0,
      status: 'active',
      schedule: '—',
      plannedMin: 0,
      audioMode: 'group',
    });
  });

  DB.users = Array.from(usersById.values());
  DB.circles = circles;
  DB.sessions = sessions;

  // --- contributions from the operator's own purchases tied to a meditation ---
  const contributions: Row[] = [];
  (bundle.purchases || []).forEach((p) => {
    if (!p.meditation_id || p.amount_minor == null) return;
    contributions.push({
      id: 'c_' + p.purchase_id,
      circleId: p.meditation_id,
      sessionId: 's_' + p.meditation_id,
      hostId: null,
      orgId: LIVE_ORG_ID,
      deptId: null,
      amount: p.amount_minor / 100,
      date: dateOnly(p.created_at),
      contributors: 1,
    });
  });
  DB.contributions = contributions;

  // --- admin domains with no backend query → empty (honest) ---
  DB.payouts = [];
  DB.sars = [];
  DB.moderation = [];
  DB.announcements = [];
  DB.tickets = [];
  DB.invoices = [];
  DB.audit = [];
  DB.consoleNotes = [];

  // Point the console at the operator (real backend uid); never land on host preview.
  try {
    const cur = JSON.parse(localStorage.getItem(CONSOLE_LS) || '{}');
    cur.userId = operator.id;
    if (cur.route === 'hostpreview') cur.route = 'overview';
    cur.membershipId = null;
    localStorage.setItem(CONSOLE_LS, JSON.stringify(cur));
  } catch {
    /* ignore */
  }
}

/** Read the live operator id the console is pointed at (the real backend uid). */
export function liveOperatorId(): string | null {
  try {
    const cur = JSON.parse(localStorage.getItem(CONSOLE_LS) || '{}');
    return cur.userId || null;
  } catch {
    return null;
  }
}

/** Build a host-scoped scope for the operator so the REAL host view (HostApp)
 *  renders their own live circles. The store is already live; no injection. */
export function buildHostScope(): Record<string, unknown> | null {
  const DB = (window as unknown as { DB?: { users: Array<{ id: string; name: string; avatar: [string, string] }> } }).DB;
  const id = liveOperatorId();
  if (!DB || !id) return null;
  const user = DB.users.find((u) => u.id === id) || { id, name: 'You', avatar: ['#95CEF6', '#6CC2F0'] as [string, string] };
  return {
    level: 'host',
    orgId: LIVE_ORG_ID,
    deptId: null,
    hostId: id,
    label: user.name,
    sublabel: 'Yperea · host',
    role: 'host',
    user,
    memberships: [],
    activeId: 'host',
    accent: '#95CEF6',
    canCrossOrg: false,
    canManageOrgs: false,
    canApprovePayouts: false,
    canManageBilling: false,
    canModerate: false,
    canManageUsers: false,
    canBroadcast: false,
  };
}
